/**
 * playhead_tracker — lets non-React code (action thunks) read the editor
 * timeline's current playhead position without threading it through Redux.
 *
 * IdeaTimeline registers a getter on mount; addBoxThunk/addSound consult it
 * so newly added elements are inserted at the playhead instead of frame 0.
 * In the visor (or before the timeline mounts) no getter is registered and
 * getEditorPlayhead() returns null — callers fall back to legacy behavior.
 */

let getter = null;

export function registerPlayheadTracker(fn) {
  getter = fn;
}

export function unregisterPlayheadTracker(fn) {
  if (getter === fn) getter = null;
}

/** @returns {{ frame: number, sceneId: string|number, playing: boolean, advancing: boolean, seekId: number }|null} */
export function getEditorPlayhead() {
  if (!getter) return null;
  try {
    const v = getter();
    return v && typeof v.frame === "number" ? v : null;
  } catch (e) {
    return null;
  }
}

let seeker = null;

/** IdeaTimeline registers its seekToFrame here (frame → playhead + animations). */
export function registerPlayheadSeeker(fn) {
  seeker = fn;
}

export function unregisterPlayheadSeeker(fn) {
  if (seeker === fn) seeker = null;
}

const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => resolve()));

/**
 * Show second `sec` of scene `navId` on the editor canvas — used by the build chat
 * (previewScene) and sceneCapture. Opens the scene first when another one is open.
 * @returns {Promise<boolean>} false when no timeline is mounted or the scene did not open.
 */
export async function seekEditorPlayhead(navId, sec) {
  if (!seeker) return false;
  const isOpen = () => {
    const p = getEditorPlayhead();
    return !!p && String(p.sceneId) === String(navId);
  };
  if (!isOpen()) {
    // Lazy: actions.es6 imports this module (addBoxThunk reads the playhead).
    const { getStore } = require("./environment_config");
    const { selectNavItem } = require("./actions");
    getStore().dispatch(selectNavItem(navId));
    for (let i = 0; i < 10 && !isOpen(); i++) await nextFrame();
    if (!isOpen()) return false;
  }
  const { FPS } = require("./sceneTimeline.es6");
  seeker(Math.max(0, Math.round((Number(sec) || 0) * FPS)));
  return true;
}
